import { ReactNode } from 'react';
import { ArrowLeft, ArrowRight, LucideIcon } from 'lucide-react';
import Link from 'next/link';

import { Button } from '@/shared/components/ui/Button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/shared/utils/cn';

export interface NotFoundLink {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  href: string;
}

export interface NotFoundProps {
  /** Error code (404, 500, 403, 401, etc.) */
  errorCode?: string | number;
  /** Main heading text */
  title?: string;
  /** Subtitle/description text */
  description?: string;
  /** Links to display below the main content */
  links?: NotFoundLink[];
  /** Handler for back button click */
  onBackClick?: () => void;
  /** Handler for home button click */
  onHomeClick?: () => void;
  /** Custom back button text */
  backButtonText?: string;
  /** Custom home button text */
  homeButtonText?: string;
  /** Show the grid background pattern */
  showBackground?: boolean;
  /** Additional CSS classes for the main container */
  className?: string;
  /** Children to render instead of default content */
  children?: ReactNode;
}

// Helper function to get error-specific translations
function getErrorTranslations(errorCode: string | number | undefined, t: (key: string) => string) {
  const code = errorCode?.toString();

  // Check if we have specific translations for this error code
  if (code && ['404', '500', '403', '401'].includes(code)) {
    return {
      title: t(`error.${code}.title`),
      description: t(`error.${code}.description`),
      errorCode: t(`error.${code}.errorCode`),
    };
  }

  // Fallback to default error translations
  return {
    title: t('error.default.title'),
    description: t('error.default.description'),
    errorCode: t('error.default.errorCode'),
  };
}

export function NotFound({
  errorCode,
  title,
  description,
  links = [],
  onBackClick,
  onHomeClick,
  backButtonText,
  homeButtonText,
  showBackground = true,
  className,
  children,
}: NotFoundProps) {
  const { t } = useTranslation();

  // Get error-specific translations
  const errorTranslations = getErrorTranslations(errorCode, t);

  return (
    <main
      className={cn(
        'flex h-screen w-full items-start justify-center px-4 py-16 md:items-center md:px-20 md:py-24',
        className,
      )}
    >
      {showBackground && (
        <div className="fixed inset-0 z-0 bg-[image:linear-gradient(to_right,var(--muted-foreground),transparent_1px),linear-gradient(to_bottom,var(--muted-foreground),transparent_1px)] [mask-image:radial-gradient(ellipse_60%_30%_at_50%_0%,black_0%,transparent_100%)] [background-size:32px_32px] opacity-50 md:[mask-image:radial-gradient(ellipse_30%_30%_at_50%_20%,black_0%,transparent_100%)] md:[background-size:48px_48px]" />
      )}

      <section className="z-10 flex flex-col items-center gap-8 md:gap-16">
        {children || (
          <>
            <div className="flex flex-col items-center gap-8 md:gap-12">
              <header className="flex flex-col items-center gap-4">
                <div>
                  <div className="border-input bg-background flex items-center gap-2 rounded-full border px-2.5 py-1 text-sm font-medium">
                    <div className="bg-primary-500 size-2 rounded-full">
                      <div className="bg-primary-500 size-2 animate-ping rounded-full" />
                    </div>
                    {errorTranslations.errorCode}
                  </div>
                </div>
                <div className="flex flex-col items-center gap-4 md:gap-6">
                  <h1 className="text-center text-4xl font-semibold md:text-6xl">
                    {title ?? errorTranslations.title}
                  </h1>
                  <p className="text-muted-foreground text-center text-lg md:text-xl">
                    {description ?? errorTranslations.description}
                  </p>
                </div>
              </header>
              <div className="flex w-full flex-col items-center justify-center gap-3 md:flex-row">
                <Button className="w-full md:w-fit" variant="outline" onClick={onBackClick}>
                  <ArrowLeft className="h-4 w-4" />
                  {backButtonText ?? t('error.backButtonText')}
                </Button>
                <Button className="w-full md:w-fit" onClick={onHomeClick}>
                  {homeButtonText ?? t('error.homeButtonText')}
                </Button>
              </div>
            </div>

            {links.length > 0 && (
              <div className="flex w-full flex-col divide-y border-t border-b">
                {links.map(link => (
                  <Link
                    href={link.href}
                    key={link.title}
                    className="hover:bg-muted/50 flex flex-col items-start gap-4 py-5 transition-colors md:flex-row md:items-center md:gap-5"
                  >
                    <div className="bg-card rounded-lg border p-2.5 md:p-3">
                      <link.icon className="size-5 md:size-6" />
                    </div>
                    <div className="flex w-full flex-1 gap-5">
                      <div className="flex flex-col gap-1">
                        <div className="text-lg font-semibold">{link.title}</div>
                        <div className="text-muted-foreground">{link.subtitle}</div>
                      </div>
                      <div className="ml-auto self-start">
                        <ArrowRight className="text-muted-foreground h-4 w-4" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
